import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, LoaderCircle, XCircle } from 'lucide-react';

import { onboardingApi } from '@/api/onboarding/onboarding';
import { useGetWorkspacesByTenant } from '@/api/workspaces/useGetWorkspacesByTenant';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace, setSelectedWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';
import { getMockBearerToken } from '@/utils/auth/mockAuthSession';
import { AppRoutes } from '@/utils/routes/router';

const StripeCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const { data: workspaces, refetch: refetchWorkspaces } = useGetWorkspacesByTenant();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Ensure workspace is selected before processing callback
    if (!workspace && workspaces && workspaces.length > 0) {
      const firstWorkspace = workspaces[0];
      dispatch(setSelectedWorkspace(firstWorkspace));
      sessionStorage.setItem('workspace', JSON.stringify(firstWorkspace));
    } else if (!workspace) {
      // Try to get from sessionStorage
      const storedWorkspace = sessionStorage.getItem('workspace');
      if (storedWorkspace) {
        try {
          const parsed = JSON.parse(storedWorkspace);
          dispatch(setSelectedWorkspace(parsed));
        } catch {
          // ignore parse errors
        }
      }
    }
  }, [workspace, workspaces, dispatch]);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }

    // Wait for workspaces to load if they're not available yet
    if (!workspace && (!workspaces || workspaces.length === 0)) {
      // Workspaces might still be loading, wait a bit
      return;
    }

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Mark as processed immediately to prevent re-execution
      hasProcessed.current = true;

      // Handle OAuth errors from Stripe
      if (error) {
        setStatus('error');

        // User-friendly error messages
        if (error === 'access_denied') {
          setErrorMessage('Stripe connection was cancelled. Please try again if you want to connect your account.');
        } else if (error === 'invalid_scope') {
          setErrorMessage('Invalid permissions requested. Please contact support.');
        } else {
          setErrorMessage(errorDescription || `Stripe OAuth error: ${error}`);
        }

        // Redirect back to onboarding after 5 seconds - use replace to avoid history buildup
        setTimeout(() => {
          navigate(AppRoutes.onboardingForm, { replace: true });
        }, 5000);
        return;
      }

      // Check if we have an authorization code
      if (!code) {
        setStatus('error');
        setErrorMessage('No authorization code received from Stripe. Please try connecting again.');

        // Redirect back to onboarding after 5 seconds - use replace to avoid history buildup
        setTimeout(() => {
          navigate(AppRoutes.onboardingForm, { replace: true });
        }, 5000);
        return;
      }

      try {
        setStatus('loading');
        setMessage('Connecting your Stripe account...');

        // Get workspace ID - try multiple sources
        const getWorkspaceId = (): string => {
          // Helper to extract workspace ID
          const extractWorkspaceId = (workspaceObj: any) => {
            if (!workspaceObj) return '';
            if (workspaceObj.id) return workspaceObj.id;
            if (workspaceObj.SK) {
              if (workspaceObj.SK.startsWith('WS#')) {
                return workspaceObj.SK.substring(3);
              }
              return workspaceObj.SK;
            }
            return '';
          };

          // Try Redux store first
          const reduxWorkspaceId = extractWorkspaceId(workspace);
          if (reduxWorkspaceId) return reduxWorkspaceId;

          // Try sessionStorage
          const storedWorkspace = sessionStorage.getItem('workspace');
          if (storedWorkspace) {
            try {
              const parsed = JSON.parse(storedWorkspace);
              const workspaceId = extractWorkspaceId(parsed);
              if (workspaceId) return workspaceId;
            } catch {
              // ignore parse errors
            }
          }

          // Try workspaces array
          if (workspaces && workspaces.length > 0) {
            const workspaceId = extractWorkspaceId(workspaces[0]);
            if (workspaceId) return workspaceId;
          }

          throw new Error('No workspace selected. Please go back to onboarding and select a workspace first.');
        };

        const workspaceId = getWorkspaceId();

        // Get token
        const resolveToken = async (): Promise<string> => {
          if (user?.signInUserSession?.idToken?.jwtToken) {
            return user.signInUserSession.idToken.jwtToken;
          }
          const jwt = getMockBearerToken();
          if (jwt) return jwt;
          throw new Error('Authentication token is required');
        };

        const token = await resolveToken();

        // Call backend to connect Stripe with OAuth code
        const result = await onboardingApi.connectStripeOAuth(token, workspaceId, code);

        setStatus('success');
        setMessage(result.message || 'Stripe account connected successfully!');

        // Redirect to onboarding form after 3 seconds to show success state - use replace to avoid history buildup
        setTimeout(() => {
          // Pass success state via location state
          navigate(AppRoutes.onboardingForm, {
            replace: true,
            state: {
              stripeConnected: true,
              stripeAccountId: result.stripeAccountId,
            },
          });
        }, 3000);
      } catch (error) {
        setStatus('error');
        const errorMsg = error instanceof Error ? error.message : 'Failed to connect Stripe account. Please try again.';
        setErrorMessage(errorMsg);

        // Redirect back to onboarding after 5 seconds - use replace to avoid history buildup
        setTimeout(() => {
          navigate(AppRoutes.onboardingForm, { replace: true });
        }, 5000);
      }
    };

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, workspace, workspaces]); // Wait for workspace/workspaces to be available

  return (
    <div className="min-h-screen bg-custom-silver p-4 flex items-center justify-center">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="flex justify-center mb-4">
                <LoaderCircle size={48} className="text-custom-blue animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-custom-black mb-2">Connecting Stripe...</h2>
              <p className="text-custom-darkSilver">{message || 'Please wait while we connect your account'}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-4">
                <CheckCircle2 size={48} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-2">Successfully Connected!</h2>
              <p className="text-green-700 mb-4">{message}</p>
              <p className="text-sm text-custom-darkSilver">Redirecting you back...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <XCircle size={48} className="text-custom-red" />
              </div>
              <h2 className="text-2xl font-bold text-custom-red mb-2">Connection Failed</h2>
              <p className="text-custom-red mb-4">{errorMessage}</p>
              <p className="text-sm text-custom-darkSilver">Redirecting you back...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripeCallbackPage;
